import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Upload, MoreVertical, Trash2, Flame, Snowflake, Thermometer, ArrowRight, ScanLine, FileSignature, BadgeCheck, Zap, CalendarClock, MapPin, CheckCircle2, Clock, Sparkles, ClipboardPaste } from "lucide-react";
import { useDragKanban } from "@/hooks/useDragKanban";
import { useSearchParams } from "react-router-dom";

const STAGES = [
  { id: "novo", label: "Novo", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { id: "em_atendimento", label: "Em atendimento", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  { id: "qualificado", label: "Qualificado", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { id: "visita", label: "Visita", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { id: "proposta", label: "Proposta", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { id: "fechamento", label: "Fechamento", color: "bg-lime-100 text-lime-700 border-lime-200" },
  { id: "ganho", label: "Ganho", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { id: "perdido", label: "Perdido", color: "bg-stone-200 text-stone-600 border-stone-300" },
];

const TEMPS = {
  quente: { icon: Flame, color: "text-red-600", label: "Quente" },
  morno: { icon: Thermometer, color: "text-amber-600", label: "Morno" },
  frio: { icon: Snowflake, color: "text-blue-600", label: "Frio" },
};

const CHANNELS = {
  instagram: { label: "Instagram", cls: "bg-pink-100 text-pink-700 border-pink-200" },
  meta: { label: "Meta Ads", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  facebook: { label: "Facebook", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  olx: { label: "OLX", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  zap: { label: "Zap", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  vivareal: { label: "VivaReal", cls: "bg-teal-100 text-teal-700 border-teal-200" },
  indicacao: { label: "Indicação", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  site: { label: "Site", cls: "bg-stone-100 text-stone-600 border-stone-200" },
  google: { label: "Google", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  youtube: { label: "YouTube", cls: "bg-red-100 text-red-700 border-red-200" },
  tiktok: { label: "TikTok", cls: "bg-stone-800 text-white border-stone-700" },
};

const EMPTY_LEAD = {
  name: "", phone: "", interest: "", neighborhood: "",
  budget: 0, financing: "", bedrooms: 0, notes: "",
  stage: "novo", temperature: "morno",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tempFilter = searchParams.get("temp") || "todos";
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_LEAD);
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_LEAD); setEditingId(null); setDialogOpen(true);
  };

  const [dossier, setDossier] = useState(null);

  const openEdit = (lead) => {
    setForm({ ...EMPTY_LEAD, ...lead });
    setEditingId(lead.id);
    setDialogOpen(true);
    setDossier(null);
    api.get(`/leads/${lead.id}/dossier`).then((r) => setDossier(r.data)).catch(() => {});
  };

  const reloadDossier = () => {
    if (editingId) api.get(`/leads/${editingId}/dossier`).then((r) => setDossier(r.data)).catch(() => {});
  };

  const completeVisit = async (visit) => {
    try {
      await api.patch(`/tasks/${visit.task_id}`, { done: true });
      toast.success("Visita marcada como realizada");
      reloadDossier(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar visita");
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    const { next_action, next_visit, qualification, visits_count, ...payload } = form;
    try {
      if (editingId) {
        await api.patch(`/leads/${editingId}`, payload);
        toast.success("Lead atualizado");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead criado");
      }
      setDialogOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir esse lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead excluído");
    load();
  };

  const moveStage = async (lead, newStage) => {
    if (newStage === "ganho" && !lead.contract_signed_at) {
      toast.error("Ganho só depois do contrato assinado. Use 'Contrato assinado' no menu do lead.");
      return;
    }
    try {
      await api.patch(`/leads/${lead.id}`, { stage: newStage });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao mover lead");
    }
  };

  const markContractSigned = async (lead) => {
    if (!confirm(`Confirmar contrato assinado do ${lead.name}? O lead vai pra Ganho.`)) return;
    try {
      await api.post(`/leads/${lead.id}/contract-signed`);
      toast.success(`Contrato assinado! ${lead.name} foi pra Ganho.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar contrato");
    }
  };

  const dnd = useDragKanban(async (leadId, fromStage, toStage) => {
    const lead = leads.find((l) => l.id === leadId);
    if (toStage === "ganho" && !lead?.contract_signed_at) {
      toast.error("Ganho só depois do contrato assinado. Use 'Contrato assinado' no menu do lead.");
      return;
    }
    try {
      await api.patch(`/leads/${leadId}`, { stage: toStage });
      toast.success(`Movido para ${STAGES.find(s => s.id === toStage)?.label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao mover lead");
    }
  });

  const [extractDialog, setExtractDialog] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const handleExtractImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    setExtractDialog(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/extract-from-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.found_lead === false) {
        toast.error("Não consegui identificar um lead nessa imagem. Tenta outro print?");
        setExtractDialog(false);
      } else {
        setExtracted(data);
        setForm({ ...EMPTY_LEAD, ...data });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao analisar imagem");
      setExtractDialog(false);
    } finally {
      setExtracting(false);
      e.target.value = "";
    }
  };

  const confirmExtracted = () => {
    setExtractDialog(false);
    setEditingId(null);
    setDialogOpen(true);
  };

  const [smartBusy, setSmartBusy] = useState(false);
  const [smartResult, setSmartResult] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const runSmartImport = async (fd) => {
    setSmartBusy(true);
    try {
      const { data } = await api.post("/leads/import-smart", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.leads?.length) {
        toast.error("Não encontrei nenhum lead nesse conteúdo");
        return;
      }
      setSmartResult(data);
      setPasteOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na importação inteligente");
    } finally { setSmartBusy(false); }
  };

  const handleSmartImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await runSmartImport(fd);
    e.target.value = "";
  };

  const handlePasteImport = async () => {
    if (!pasteText.trim()) { toast.error("Cola a lista de leads primeiro"); return; }
    const fd = new FormData();
    fd.append("text", pasteText);
    await runSmartImport(fd);
  };

  const confirmSmartImport = async () => {
    setSmartBusy(true);
    try {
      const { data } = await api.post("/leads/import-confirm", { leads: smartResult.leads });
      toast.success(`${data.created ?? data.inserted ?? 0} leads cadastrados`);
      setSmartResult(null); setPasteText(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao cadastrar leads");
    } finally { setSmartBusy(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} leads importados!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const visibleLeads = tempFilter === "todos"
    ? leads
    : leads.filter((l) => (l.temperature || "morno") === tempFilter);

  const byStage = (s) => visibleLeads.filter((l) => l.stage === s);

  const tempCount = (t) => leads.filter((l) => (l.temperature || "morno") === t).length;

  const setTempFilter = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "todos") next.delete("temp"); else next.set("temp", t);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-5 lg:p-10 max-w-7xl mx-auto" data-testid="leads-root">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Carteira</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C19]">Leads</h1>
          <p className="text-stone-500 mt-1 text-sm">{leads.length} no total · arraste pelo funil</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="image/*" onChange={handleExtractImage} className="hidden" data-testid="extract-image-input" />
            <Button variant="outline" disabled={extracting} className="border-stone-300" asChild>
              <span>
                <ScanLine className="w-4 h-4 mr-2" />
                {extracting ? "Lendo print..." : "Importar print"}
              </span>
            </Button>
          </label>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
            <Button variant="outline" disabled={importing} className="border-stone-300" asChild>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importando..." : "CSV/Excel"}
              </span>
            </Button>
          </label>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.txt" onChange={handleSmartImport} className="hidden" data-testid="smart-import-input" />
            <Button variant="outline" disabled={smartBusy} className="border-[#D4651A]/40 text-[#D4651A]" asChild>
              <span>
                <Sparkles className="w-4 h-4 mr-2" />
                {smartBusy ? "Lendo arquivo..." : "Importação inteligente"}
              </span>
            </Button>
          </label>
          <Button variant="outline" onClick={() => setPasteOpen(true)} data-testid="paste-leads-btn" className="border-stone-300">
            <ClipboardPaste className="w-4 h-4 mr-2" /> Colar lista
          </Button>
          <Button data-testid="add-lead-btn" onClick={openCreate} className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white">
            <Plus className="w-4 h-4 mr-2" /> Novo lead
          </Button>
        </div>
      </div>

      {/* Filtro por temperatura */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="temperature-filter">
        {[
          { id: "todos", label: "Todos", icon: null },
          { id: "quente", label: "Quentes", icon: Flame },
          { id: "morno", label: "Mornos", icon: Thermometer },
          { id: "frio", label: "Frios", icon: Snowflake },
        ].map((t) => {
          const Icon = t.icon;
          const active = tempFilter === t.id;
          const count = t.id === "todos" ? leads.length : tempCount(t.id);
          return (
            <button
              key={t.id}
              data-testid={`temp-filter-${t.id}`}
              onClick={() => setTempFilter(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition ${
                active
                  ? "bg-[#1B3A2F] text-white border-[#1B3A2F]"
                  : "bg-white text-stone-600 border-stone-300 hover:border-[#1B3A2F]/50"
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />} {t.label}
              <span className={`text-xs ${active ? "text-white/70" : "text-stone-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Kanban */}
      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4">
        {STAGES.map((s) => {
          const list = byStage(s.id);
          return (
            <div
              key={s.id}
              className={`flex-shrink-0 w-64 rounded-lg transition ${dnd.overStage === s.id ? 'bg-[#1B3A2F]/8 ring-2 ring-[#1B3A2F]/30' : ''}`}
              data-testid={`kanban-column-${s.id}`}
              {...dnd.column(s.id)}
            >
              <div className={`px-3 py-2 rounded-lg border ${s.color} flex items-center justify-between mb-3`}>
                <div className="font-semibold text-sm">{s.label}</div>
                <Badge variant="secondary" className="bg-white/70">{list.length}</Badge>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {list.length === 0 ? (
                  <div className="text-xs text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-lg">
                    Arrasta um card aqui
                  </div>
                ) : list.map((lead) => (
                  <div key={lead.id} {...dnd.card(lead.id, s.id)}>
                    <LeadCard lead={lead} onClick={() => openEdit(lead)} onMove={moveStage} onDelete={remove} onContractSigned={markContractSigned} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Importação inteligente: colar lista */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent data-testid="paste-leads-dialog">
          <DialogHeader><DialogTitle>Colar lista de leads</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">
            Cola aqui a lista que você recebeu (WhatsApp, e-mail, planilha copiada, anotação).
            Eu identifico nome, telefone, interesse, bairro e orçamento de cada pessoa.
          </p>
          <Textarea data-testid="paste-leads-textarea" rows={8} value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Ex.:\nMarcos 48 99999-1111 apto 2 dorm Centro até 600 mil\nAna Paula (48) 98888-2222 casa Campeche 900k"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteOpen(false)}>Cancelar</Button>
            <Button data-testid="paste-leads-extract-btn" onClick={handlePasteImport} disabled={smartBusy}
              className="bg-[#D4651A] hover:bg-[#B85512] text-white">
              {smartBusy ? "Lendo..." : "Extrair leads"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Importação inteligente: revisão */}
      <Dialog open={!!smartResult} onOpenChange={(v) => !v && setSmartResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="smart-import-review-dialog">
          <DialogHeader>
            <DialogTitle>{smartResult?.count} lead(s) encontrado(s) · {smartResult?.source_kind}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">Revisa antes de cadastrar. Confiança: {smartResult?.confidence}.</p>
          <div className="space-y-2">
            {smartResult?.leads?.map((l, i) => (
              <div key={i} data-testid={`smart-import-lead-${i}`} className="rounded-lg border border-stone-200 p-2.5 text-sm flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-[#1B3A2F]">{l.name}</div>
                  <div className="text-xs text-stone-500">
                    {[l.phone, l.interest, l.neighborhood, l.budget ? formatBRL(l.budget) : "", l.bedrooms ? `${l.bedrooms} dorm` : ""]
                      .filter(Boolean).join(" · ")}
                  </div>
                  {l.notes && <div className="text-xs text-stone-400">{l.notes}</div>}
                </div>
                <button data-testid={`smart-import-remove-${i}`} className="text-stone-400 hover:text-red-600"
                  onClick={() => setSmartResult({ ...smartResult, leads: smartResult.leads.filter((_, j) => j !== i), count: smartResult.leads.length - 1 })}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmartResult(null)}>Cancelar</Button>
            <Button data-testid="smart-import-confirm-btn" onClick={confirmSmartImport}
              disabled={smartBusy || !smartResult?.leads?.length}
              className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white">
              Cadastrar {smartResult?.leads?.length || 0} lead(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog */}
      <Dialog open={extractDialog} onOpenChange={setExtractDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{extracting ? "Analisando imagem..." : "Lead extraído"}</DialogTitle></DialogHeader>
          {extracting ? (
            <div className="py-8 text-center text-stone-500">
              <ScanLine className="w-10 h-10 mx-auto mb-3 text-[#D4651A] animate-pulse" />
              Lendo o print, identificando nome, telefone, interesse...
            </div>
          ) : extracted ? (
            <div className="space-y-2 text-sm">
              {extracted.name && <Row label="Nome" value={extracted.name} />}
              {extracted.phone && <Row label="Telefone" value={extracted.phone} />}
              {extracted.interest && <Row label="Interesse" value={extracted.interest} />}
              {extracted.neighborhood && <Row label="Bairro" value={extracted.neighborhood} />}
              {extracted.budget && <Row label="Orçamento" value={`R$ ${Number(extracted.budget).toLocaleString('pt-BR')}`} />}
              {extracted.bedrooms && <Row label="Dormitórios" value={extracted.bedrooms} />}
              {extracted.financing && <Row label="Pagamento" value={extracted.financing} />}
              <p className="text-xs text-stone-500 pt-2">Confiança: <strong>{extracted.confidence || 'media'}</strong>. Revisa e ajusta o que precisar antes de salvar.</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtractDialog(false)}>Cancelar</Button>
            {extracted && <Button onClick={confirmExtracted} className="bg-[#1B3A2F] hover:bg-[#244A3C]">Revisar e salvar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar lead" : "Novo lead"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {editingId && dossier?.next_action && (
              <div data-testid="lead-next-action-banner"
                className="rounded-xl border border-[#D4651A]/30 bg-[#D4651A]/5 p-3 flex items-start gap-2.5">
                <Zap className="w-4 h-4 text-[#D4651A] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-[#D4651A]">Próxima ação</div>
                  <div className="text-sm text-stone-800 font-medium">{dossier.next_action.text}</div>
                </div>
              </div>
            )}
            {editingId && dossier?.visits?.length > 0 && (
              <div data-testid="lead-visits-section" className="rounded-xl border border-stone-200 p-3 space-y-2.5">
                <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Visitas ({dossier.visits.length})
                </div>
                {dossier.visits.map((v) => (
                  <div key={v.task_id} data-testid={`lead-visit-${v.task_id}`}
                    className="rounded-lg bg-stone-50 border border-stone-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold text-[#1B3A2F] flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-stone-400" />
                        {v.date} às {v.time}
                      </div>
                      <Badge className={
                        v.status === "realizada" ? "bg-emerald-100 text-emerald-800"
                        : v.status === "atrasada" ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-800"
                      }>{v.status_label}</Badge>
                    </div>
                    {(v.property_title || v.neighborhood) && (
                      <div className="text-xs text-stone-600 mt-1">
                        Imóvel: {v.property_title || "não identificado"}
                        {v.neighborhood ? ` · ${v.neighborhood}` : ""}
                      </div>
                    )}
                    {v.address && (
                      <div className="text-xs text-stone-600 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {v.address}
                      </div>
                    )}
                    <div className="text-xs text-stone-500 mt-0.5">
                      Cliente: {v.client_name}{v.client_phone ? ` · ${v.client_phone}` : ""}
                      {v.realtor_name ? ` · Corretor: ${v.realtor_name}` : ""}
                    </div>
                    {v.notes && <div className="text-xs text-stone-500 mt-0.5">Obs: {v.notes}</div>}
                    <div className="text-xs text-[#D4651A] font-medium mt-1">→ {v.next_action}</div>
                    {v.status !== "realizada" && (
                      <Button size="sm" variant="outline" data-testid={`complete-visit-${v.task_id}`}
                        onClick={() => completeVisit(v)} className="mt-2 h-7 text-xs border-emerald-300 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Marcar como realizada
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {editingId && dossier?.qualification && !dossier.qualification.ready && (
              <div data-testid="lead-qualification-gaps" className="text-xs text-stone-500">
                Falta pra qualificar: {dossier.qualification.missing_labels.join(", ")}
              </div>
            )}
            <Field label="Nome *">
              <Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="WhatsApp">
                <Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
              </Field>
              <Field label="Bairro">
                <Input value={form.neighborhood} onChange={(e) => setForm({...form, neighborhood: e.target.value})} />
              </Field>
            </div>
            <Field label="Interesse">
              <Input placeholder="Apto 2 dorm, cobertura..." value={form.interest} onChange={(e) => setForm({...form, interest: e.target.value})} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Orçamento (R$)">
                <Input type="number" data-testid="lead-budget-input" value={form.budget} onChange={(e) => setForm({...form, budget: parseFloat(e.target.value) || 0})} />
              </Field>
              <Field label="Dorm.">
                <Input type="number" value={form.bedrooms} onChange={(e) => setForm({...form, bedrooms: parseInt(e.target.value) || 0})} />
              </Field>
              <Field label="Pagamento">
                <Input placeholder="FGTS, financ..." value={form.financing} onChange={(e) => setForm({...form, financing: e.target.value})} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Etapa">
                <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}>
                  <SelectTrigger data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Temperatura">
                <Select value={form.temperature} onValueChange={(v) => setForm({...form, temperature: v})}>
                  <SelectTrigger data-testid="lead-temperature-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quente">🔥 Quente</SelectItem>
                    <SelectItem value="morno">🌡️ Morno</SelectItem>
                    <SelectItem value="frio">❄️ Frio</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Anotações">
              <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} />
            </Field>
            {editingId && form.interests?.length > 0 && (
              <div data-testid="lead-interests-list">
                <Label className="text-xs text-stone-500">Interesses adicionais (registrados pela IA)</Label>
                <div className="space-y-1 mt-1">
                  {form.interests.map((it, i) => (
                    <div key={i} className="text-xs bg-stone-50 border border-stone-200 rounded p-2">
                      {it.interest}
                      {it.neighborhood ? ` · ${it.neighborhood}` : ""}
                      {it.budget > 0 ? ` · ${formatBRL(it.budget)}` : ""}
                      {it.bedrooms > 0 ? ` · ${it.bedrooms} dorm` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-lead-btn" onClick={save} className="bg-[#1B3A2F] hover:bg-[#244A3C]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadCard({ lead, onClick, onMove, onDelete, onContractSigned }) {
  const temp = TEMPS[lead.temperature] || TEMPS.morno;
  const TempIcon = temp.icon;
  const [clickStats, setClickStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/leads/${lead.id}/click-stats`).then((r) => { if (!cancelled) setClickStats(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [lead.id]);
  const hasClicks = clickStats?.total_clicks > 0;
  return (
    <Card
      data-testid={`lead-card-${lead.id}`}
      className={`p-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition bg-white ${hasClicks ? 'border-2 border-emerald-400 shadow-emerald-100/50 shadow-md' : 'border border-stone-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0" onClick={onClick}>
          <div className="flex items-center gap-1.5">
            <TempIcon className={`w-3.5 h-3.5 ${temp.color}`} title={lead.temperature_reason || temp.label} />
            <div className="font-semibold text-sm truncate">{lead.name}</div>
          </div>
          {lead.qualification?.suggest_stage === "qualificado" && (
            <div data-testid={`lead-qualify-hint-${lead.id}`}
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-[11px] font-semibold text-purple-800">
              <BadgeCheck className="w-3 h-3" /> Pronto pra qualificar
            </div>
          )}
          {lead.contract_signed_at && (
            <div data-testid={`lead-contract-badge-${lead.id}`}
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800">
              <FileSignature className="w-3 h-3" /> Contrato assinado
            </div>
          )}
          {lead.next_visit && (
            <div data-testid={`lead-next-visit-${lead.id}`}
              className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${
                lead.next_visit.status === "atrasada"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}>
              <CalendarClock className="w-3 h-3" /> Visita {lead.next_visit.date} · {lead.next_visit.time}
            </div>
          )}
          {lead.next_action && (
            <div data-testid={`lead-next-action-${lead.id}`}
              className="mt-1.5 flex items-start gap-1 text-[11px] text-stone-600 leading-snug">
              <Zap className="w-3 h-3 text-[#D4651A] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{lead.next_action.text}</span>
            </div>
          )}
          {lead.interest && <div className="text-xs text-stone-500 mt-0.5 truncate">{lead.interest}</div>}
          <div className="flex flex-wrap gap-1 mt-2">
            {lead.source_channel && CHANNELS[lead.source_channel] && (
              <Badge data-testid={`lead-channel-badge-${lead.id}`} className={`text-xs font-normal border ${CHANNELS[lead.source_channel].cls}`}>
                {CHANNELS[lead.source_channel].label}{lead.source_ref ? ` · ${lead.source_ref}` : ""}
              </Badge>
            )}
            {lead.neighborhood && <Badge variant="outline" className="text-xs font-normal">{lead.neighborhood}</Badge>}
            {lead.budget > 0 && <Badge variant="outline" className="text-xs font-normal">{formatBRL(lead.budget)}</Badge>}
            {lead.bedrooms > 0 && <Badge variant="outline" className="text-xs font-normal">{lead.bedrooms} dorm</Badge>}
          </div>
          {lead.interests?.length > 0 && (
            <div data-testid={`lead-interests-count-${lead.id}`} className="text-[11px] text-stone-400 mt-1">
              +{lead.interests.length} interesse{lead.interests.length > 1 ? "s" : ""} adiciona{lead.interests.length > 1 ? "is" : "l"}
            </div>
          )}
          {hasClicks && (
            <div data-testid={`lead-click-badge-${lead.id}`} className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-bold text-emerald-800">
                {clickStats.total_clicks}× {clickStats.total_clicks === 1 ? 'clique' : 'cliques'}
              </span>
              <span className="text-[10px] text-emerald-700 truncate">
                em {clickStats.unique_properties} {clickStats.unique_properties === 1 ? 'imóvel' : 'imóveis'}
              </span>
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`lead-menu-${lead.id}`} className="p-1 text-stone-400 hover:text-stone-700">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {lead.stage !== "ganho" && (
              <DropdownMenuItem data-testid={`lead-contract-signed-${lead.id}`}
                onClick={() => onContractSigned(lead)} className="text-emerald-700 font-semibold">
                <FileSignature className="w-3.5 h-3.5 mr-2" /> Contrato assinado → Ganho
              </DropdownMenuItem>
            )}
            <div className="px-2 py-1 text-xs text-stone-500">Mover para:</div>
            {STAGES.filter((s) => s.id !== lead.stage && s.id !== "ganho").map((s) => (
              <DropdownMenuItem key={s.id} onClick={() => onMove(lead, s.id)}>
                <ArrowRight className="w-3.5 h-3.5 mr-2" /> {s.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => onDelete(lead.id)} className="text-red-600">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-stone-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-stone-100 last:border-0 py-1.5">
      <span className="text-stone-500">{label}</span>
      <span className="font-semibold text-stone-800">{value}</span>
    </div>
  );
}
